import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useRubrics = () => {
  const [rubrics, setRubrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRubrics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/rubrics`);
      setRubrics(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching rubrics:', err);
      setError('Failed to load rubrics');
      // Return empty array on auth error instead of keeping stale data
      if (err.response?.status === 401) {
        setRubrics([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const createRubric = async (rubricData) => {
    const response = await axios.post(`${API}/rubrics`, rubricData);
    setRubrics(prev => [...prev, response.data]);
    return response.data;
  };

  const updateRubric = async (rubricId, rubricData) => {
    const response = await axios.put(`${API}/rubrics/${rubricId}`, rubricData);
    setRubrics(prev => prev.map(r => r.id === rubricId ? response.data : r));
    return response.data;
  };

  const deleteRubric = async (rubricId) => {
    await axios.delete(`${API}/rubrics/${rubricId}`);
    setRubrics(prev => prev.filter(r => r.id !== rubricId));
  };

  const duplicateRubric = async (rubricId, studentId = null) => {
    const response = await axios.post(`${API}/rubrics/${rubricId}/duplicate`, null, {
      params: studentId ? { student_id: studentId } : {}
    });
    setRubrics(prev => [...prev, response.data]);
    return response.data;
  };

  useEffect(() => {
    fetchRubrics();
  }, [fetchRubrics]);

  return {
    rubrics,
    loading,
    error,
    fetchRubrics,
    createRubric,
    updateRubric,
    deleteRubric,
    duplicateRubric
  };
};

export const useRubricPresets = () => {
  const [presets, setPresets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [presetsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/rubric-presets`),
          axios.get(`${API}/rubric-presets/categories`)
        ]);
        setPresets(presetsRes.data);
        setCategories(categoriesRes.data);
      } catch (err) {
        console.error('Error fetching presets:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getPresetsByCategory = useCallback((category) => {
    return presets.filter(p => p.category.toLowerCase() === category.toLowerCase());
  }, [presets]);

  return {
    presets,
    categories,
    loading,
    getPresetsByCategory
  };
};

export const useAISuggest = () => {
  const [loading, setLoading] = useState(false);

  const suggestRubric = useCallback(async (goalText, category = null, numLevels = 4) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/suggest-rubric`, {
        goal_text: goalText,
        category: category,
        num_levels: numLevels
      });
      return response.data;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    suggestRubric,
    loading
  };
};
